import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Search,
  Eye,
  PowerOff,
  RefreshCw,
  UserCheck,
  CreditCard,
  Users,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'sonner';
import api from '../../services/api';

const BusinessesActive = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    owner: '',
    plan: ''
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);

      const { data } = await api.get('/admin/businesses', { params: { status: 'active', limit: 100 } });
      setBusinesses(data.businesses || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load businesses'));
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = (businessId) => {
    setConfirmModal({
      title: 'Suspend Business',
      description: 'This will immediately block all access for the business owner and staff.',
      confirmLabel: 'Suspend',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        try {
          await api.patch(`/admin/businesses/${businessId}/status`, null, { params: { status: 'suspended' } });
          toast.success('Business suspended successfully');
          fetchBusinesses();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to suspend business'));
        }
      }
    });
  };

  // Apply filters
  const filteredBusinesses = businesses.filter(business => {
    const matchesSearch = !searchTerm || 
      business.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.owner_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesName = !columnFilters.name || 
      business.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
    
    const matchesOwner = !columnFilters.owner || 
      business.owner_name?.toLowerCase().includes(columnFilters.owner.toLowerCase());
    
    const matchesPlan = !columnFilters.plan || 
      business.plan_name?.toLowerCase().includes(columnFilters.plan.toLowerCase());

    return matchesSearch && matchesName && matchesOwner && matchesPlan;
  });

  if (loading) {
    return (
      <div className="h-full flex flex-col" data-testid="active-businesses-page">
        <ListPageHeader title="Active Businesses" stats={[]} />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-[3px] border-slate-200 border-t-rose-500 rounded-full animate-spin"></div>
            <p className="text-[13px] text-slate-500 font-normal">Loading businesses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="active-businesses-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Active Businesses"
        stats={[
          { icon: UserCheck, label: 'Active', value: totalCount, color: 'emerald' },
        ]}
        ctaLabel="Refresh"
        ctaIcon={RefreshCw}
        onCtaClick={fetchBusinesses}
        extraControls={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search businesses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-[13px] w-56 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400"
            />
          </div>
        }
      />

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {filteredBusinesses.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <UserCheck className="w-7 h-7 text-emerald-500" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No active businesses</p>
              <p className="text-[13px] text-slate-400 mt-1">Active businesses will appear here</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Table Header */}
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)', width: '25%'}}>
                        <div className="flex items-center gap-1">
                          Business Name
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.name}
                            onChange={(val) => setColumnFilters({...columnFilters, name: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)', width: '18%'}}>
                        <div className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          Owner
                          <ColumnFilter
                            type="text"
                            value={columnFilters.owner}
                            onChange={(val) => setColumnFilters({...columnFilters, owner: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)', width: '15%'}}>
                        <div className="flex items-center gap-1">
                          <CreditCard className="w-3.5 h-3.5" />
                          Plan
                        </div>
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)', width: '15%'}}>
                        Created
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)', width: '12%'}}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Table Body */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody className="animate-stagger">
                    {filteredBusinesses.map((business, index) => (
                      <tr 
                        key={business.id} 
                        className={`tl-tr hover:bg-rose-50/30 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                      >
                        <td className="px-4 py-3" style={{width: '25%'}}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[13px] font-medium text-slate-800 truncate">{business.name}</div>
                              {business.email && (
                                <div className="text-[11px] text-slate-400 truncate">{business.email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[13px] text-slate-600" style={{width: '18%'}}>
                          {business.owner_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3" style={{width: '15%'}}>
                          {business.plan_name ? (
                            <span className={`px-2 py-1 rounded text-[11px] font-medium ${
                              business.plan_name.includes('Field') || business.plan_name.includes('Tracking')
                                ? 'bg-teal-100 text-teal-700' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {business.plan_name}
                            </span>
                          ) : (
                            <span className="text-[11px] text-slate-400">No plan</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[12px] text-slate-500" style={{width: '15%'}}>
                          {business.created_at ? new Date(business.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3" style={{width: '12%'}}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                                <MoreVertical className="w-4 h-4 text-slate-500" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/admin/businesses/${business.id}`)}>
                                <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleSuspend(business.id)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <PowerOff className="w-4 h-4 mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="tl-table-footer">
                Showing {filteredBusinesses.length} active businesses
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={async () => { const fn = confirmModal?.onConfirm; setConfirmModal(null); await fn?.(); }}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default BusinessesActive;
